import { useStudyText } from '@/i18n/studyText';
import React, { useEffect, useState } from 'react';
import '../dp120.css';
import {
  DoctoralProcessOverview,
  DoctoralProfileData,
  DoctoralTopicData,
  DoctoralPlanData,
  DoctoralNirdOverview,
  DoctoralPracticeData,
  DoctoralInternshipData,
  DoctoralPublication,
  DoctoralAchievement,
  DoctoralDefenseData,
  DoctoralCompletionData,
  DoctoralEnuSourcesData,
} from '../types';
import { DoctoralOverviewView } from './DoctoralOverviewView';
import { DoctoralGuideView } from './DoctoralGuideView';
import { DoctoralProfileView } from './DoctoralProfileView';
import { DoctoralTopicView } from './DoctoralTopicView';
import { DoctoralPlanView } from './DoctoralPlanView';
import { DoctoralNirdView } from './DoctoralNirdView';
import { DoctoralPracticesView } from './DoctoralPracticesView';
import { DoctoralInternshipView } from './DoctoralInternshipView';
import { DoctoralPublicationsView } from './DoctoralPublicationsView';
import { DoctoralAchievementsView } from './DoctoralAchievementsView';
import { DoctoralDocumentsView } from './DoctoralDocumentsView';
import { DoctoralDefenseView } from './DoctoralDefenseView';
import { DoctoralCompletionView } from './DoctoralCompletionView';
import { DoctoralSectionModes } from './DoctoralSectionModes';
import { DoctoralSourcesView } from './DoctoralSourcesView';

export type DoctoralViewKey =
  | 'overview'
  | 'guide'
  | 'profile'
  | 'topic'
  | 'plan'
  | 'nird'
  | 'practices'
  | 'internship'
  | 'publications'
  | 'achievements'
  | 'documents'
  | 'defense'
  | 'completion'
  | 'sources';

interface DoctoralWorkspaceOverlayProps {
  initialView?: DoctoralViewKey;
  renderSection?: (view: DoctoralViewKey, onNavigate: (view: string) => void) => React.ReactNode;
  onClose: () => void;
  onOpenModal: (modalId: string, payload?: unknown) => void;

  overviewData?: DoctoralProcessOverview | null;
  profileData?: DoctoralProfileData | null;
  topicData?: DoctoralTopicData | null;
  planData?: DoctoralPlanData | null;
  nirdData?: DoctoralNirdOverview | null;
  practicesData?: DoctoralPracticeData[];
  internshipData?: DoctoralInternshipData | null;
  publicationsData?: DoctoralPublication[];
  achievementsData?: DoctoralAchievement[];
  defenseData?: DoctoralDefenseData | null;
  completionData?: DoctoralCompletionData | null;
  sourcesData?: DoctoralEnuSourcesData | null;

  onSaveProfile?: (updated: DoctoralProfileData) => void;
  onUploadIup?: (file: File) => void;
  onSendNirdToAdvisor?: (semester: number) => void;
  onUploadNirdReport?: (semester: number, file: File) => void;
  onAddAchievement?: (item: DoctoralAchievement) => void;
  onOpenAcademyProfile?: () => void;
  onOpenService?: (href: string) => void;
  serviceLinks?: Record<string, string>;
}

const NAV: { key: DoctoralViewKey; ico: string; label: string; hint: string; view: string }[] = [
  {
    key: 'profile',
    ico: 'ID',
    label: 'Мой профиль',
    hint: 'Единый профиль Академии',
    view: 'profile',
  },
  {
    key: 'overview',
    ico: '▦',
    label: 'Обзор докторантуры',
    hint: 'Мой текущий маршрут',
    view: 'overview',
  },
  {
    key: 'topic',
    ico: 'T',
    label: 'Тема и консультанты',
    hint: 'От подготовки до приказа',
    view: 'topic',
  },
  {
    key: 'plan',
    ico: 'P',
    label: 'Индивидуальный план',
    hint: 'ИУП · версии · подписи',
    view: 'plan',
  },
  { key: 'nird', ico: '6', label: 'НИРД', hint: 'Научно-исследовательская работа', view: 'nird' },
  {
    key: 'practices',
    ico: 'R',
    label: 'Педагогическая и исследовательская практика',
    hint: 'По применимым семестрам',
    view: 'practice',
  },
  {
    key: 'internship',
    ico: 'S',
    label: 'Научная стажировка',
    hint: 'Подготовка · выезд · отчёт',
    view: 'internship',
  },
  {
    key: 'publications',
    ico: '∑',
    label: 'Публикации',
    hint: 'Scopus · WoS · Комитет · конференции',
    view: 'publications',
  },
  {
    key: 'achievements',
    ico: '★',
    label: 'Научные и творческие достижения',
    hint: 'Языки · курсы · награды',
    view: 'achievements',
  },
  {
    key: 'documents',
    ico: '▣',
    label: 'Образцы и видеоинструкции',
    hint: 'Открыть и скачать образец',
    view: 'resources',
  },
  {
    key: 'defense',
    ico: '✓',
    label: 'Предзащита и защита',
    hint: 'Последовательные этапы',
    view: 'defense',
  },
  {
    key: 'completion',
    ico: '◆',
    label: 'Завершение обучения',
    hint: 'Архив · отработка · диплом',
    view: 'completion',
  },
];

function personName(profile?: DoctoralProfileData | null) {
  const ru = [profile?.last_name_ru, profile?.first_name_ru, profile?.patronymic_ru]
    .filter(Boolean)
    .join(' ');
  const kz = [profile?.last_name_kz, profile?.first_name_kz].filter(Boolean).join(' ');
  return ru || kz || 'Докторант';
}

export const DoctoralWorkspaceOverlay: React.FC<DoctoralWorkspaceOverlayProps> = ({
  initialView = 'overview',
  renderSection,
  onClose,
  onOpenModal,
  overviewData,
  profileData,
  topicData,
  planData,
  nirdData,
  practicesData,
  internshipData,
  publicationsData = [],
  achievementsData = [],
  defenseData,
  sourcesData,
  onSaveProfile,
  onUploadIup,
  onSendNirdToAdvisor,
  onUploadNirdReport,
  onAddAchievement,
  onOpenAcademyProfile,
  onOpenService,
  serviceLinks = {},
}) => {
  const st = useStudyText();
  const [activeView, setActiveView] = useState<DoctoralViewKey>(initialView);
  useEffect(() => setActiveView(initialView), [initialView]);
  const readiness = overviewData?.overall_readiness ?? 0;
  const topicTitle = topicData?.topic_ru || 'Тема ещё не заполнена';
  const supervisor = topicData?.supervisor_name;
  const actions = (overviewData?.next_actions ?? []).slice(0, 4);
  const programCode = profileData?.program_code || overviewData?.program_code || '';
  const programTitle = profileData?.program_title || overviewData?.program_title || '';
  const programLine =
    [programCode, programTitle].filter(Boolean).join(' · ') || 'Программа не назначена';
  const courseYear = profileData?.current_year || overviewData?.current_year;
  const courseSemester = profileData?.current_semester || overviewData?.current_semester;
  const courseLine = courseYear
    ? `${courseYear} курс${courseSemester ? ` · ${courseSemester} семестр` : ''} · группа ${profileData?.group_name || '—'}`
    : 'Курс и группа ещё не указаны';
  const openView = (view: string) => {
    if (view === 'profile' && onOpenAcademyProfile) {
      onOpenAcademyProfile();
      return;
    }
    setActiveView(view as DoctoralViewKey);
  };

  useEffect(() => {
    if (initialView === 'profile' && onOpenAcademyProfile) onOpenAcademyProfile();
  }, [initialView, onOpenAcademyProfile]);

  const services: { label: string; href?: string; view?: DoctoralViewKey; modal?: string }[] = [
    { label: 'Журналы Комитета', href: serviceLinks.journals },
    { label: 'Конференции и семинары', href: serviceLinks.conferences },
    { label: 'PRIME', href: serviceLinks.prime },
    { label: 'Академический блокнот', href: serviceLinks.notebook },
    { label: 'Академический календарь', href: serviceLinks.calendar },
    { label: 'Мои файлы', href: serviceLinks.files },
    { label: 'Опросы', href: serviceLinks.surveys },
    { label: st('Связь с эдвайзером'), modal: 'advisor_contact' },
    { label: 'Источники и ресурсы', view: 'sources' },
    { label: 'Сообщения', href: serviceLinks.messages },
    { label: 'Помощь и FAQ', href: serviceLinks.help },
    { label: 'Настройки', href: serviceLinks.settings },
  ];

  return (
    <div className="dp120-root open">
      <div className="dp120-shell">
        <header className="dp120-top">
          <button className="dp120-back" type="button" onClick={onClose} aria-label="Назад">
            ←
          </button>
          <div className="dp120-top-copy">
            <small>Кабинет докторанта</small>
            <h1>Учебный процесс докторанта</h1>
            <p>
              Цифровое досье на весь период обучения: от поступления и темы до защиты и диплома PhD.
            </p>
          </div>
          <div className="dp120-top-right">
            <span className="dp120-save-pill">Состояние сохранения — в разделе</span>
            <button className="dp120-close" type="button" onClick={onClose} aria-label="Закрыть">
              ×
            </button>
          </div>
        </header>

        <div className="dp120-layout">
          <aside className="dp120-left">
            <div className="dp120-profile">
              <small>Личное рабочее пространство</small>
              <strong>{personName(profileData)}</strong>
              <span>
                {programLine}
                <br />
                {courseLine}
              </span>
            </div>
            <nav className="dp120-nav">
              {NAV.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  className={activeView === item.key ? 'active' : ''}
                  data-view={item.view}
                  onClick={() => openView(item.key)}
                >
                  <span className="ico">{item.ico}</span>
                  <span>
                    <strong>{item.label}</strong>
                    <small>{item.hint}</small>
                  </span>
                  <em>›</em>
                </button>
              ))}
            </nav>
          </aside>

          <main className="dp120-main">
            {renderSection ? (
              renderSection(activeView, openView)
            ) : (
              <>
                {activeView === 'overview' && (
                  <DoctoralOverviewView
                    data={overviewData || null}
                    onNavigate={openView}
                    onOpenModal={onOpenModal}
                  />
                )}
                {activeView === 'guide' && <DoctoralGuideView />}
                {activeView === 'profile' && (
                  <DoctoralProfileView data={profileData || null} onSave={onSaveProfile} />
                )}
                {activeView === 'topic' && (
                  <DoctoralTopicView
                    data={topicData || null}
                    onOpenAdvisorModal={() => onOpenModal('advisor_sample')}
                  />
                )}
                {activeView === 'plan' && (
                  <DoctoralSectionModes section="plan">
                    <DoctoralPlanView
                      data={planData || null}
                      onNavigate={openView}
                      onUploadIup={onUploadIup}
                    />
                  </DoctoralSectionModes>
                )}
                {activeView === 'nird' && (
                  <DoctoralSectionModes section="nird">
                    <DoctoralNirdView
                      data={nirdData || null}
                      onSendToAdvisor={onSendNirdToAdvisor}
                      onUploadReport={onUploadNirdReport}
                    />
                  </DoctoralSectionModes>
                )}
                {activeView === 'practices' && (
                  <DoctoralSectionModes section="practices">
                    <DoctoralPracticesView practices={practicesData} />
                  </DoctoralSectionModes>
                )}
                {activeView === 'internship' && (
                  <DoctoralSectionModes section="internship">
                    <DoctoralInternshipView data={internshipData || null} />
                  </DoctoralSectionModes>
                )}
                {activeView === 'publications' && (
                  <DoctoralSectionModes section="publications">
                    <DoctoralPublicationsView
                      publications={publicationsData}
                      onOpenAddPubModal={() => onOpenModal('add_pub')}
                    />
                  </DoctoralSectionModes>
                )}
                {activeView === 'achievements' && (
                  <DoctoralSectionModes section="achievements">
                    <DoctoralAchievementsView
                      achievements={achievementsData}
                      onAddAchievement={onAddAchievement}
                    />
                  </DoctoralSectionModes>
                )}
                {activeView === 'documents' && <DoctoralDocumentsView onNavigate={openView} />}
                {activeView === 'defense' && (
                  <DoctoralSectionModes section="defense">
                    <DoctoralDefenseView data={defenseData || null} />
                  </DoctoralSectionModes>
                )}
                {activeView === 'completion' && (
                  <DoctoralSectionModes section="completion">
                    <DoctoralCompletionView />
                  </DoctoralSectionModes>
                )}
                {activeView === 'sources' && <DoctoralSourcesView data={sourcesData || null} />}
              </>
            )}
          </main>

          <aside className="dp120-right">
            <section className="dp120-rail-card" style={{ ['--r' as string]: '#4ce2bf' }}>
              <span className="eyebrow">МОЙ МАРШРУТ</span>
              <h3>Следующие действия</h3>
              <p>Показываются только ближайшие незавершённые шаги.</p>
              <div className="dp120-next">
                {actions
                  .filter((item) => !item.is_completed)
                  .map((item) => (
                    <button
                      key={item.num + item.title}
                      type="button"
                      onClick={() => openView(item.route_key)}
                    >
                      <b>{item.num}</b>
                      <span>{item.title}</span>
                      <em>→</em>
                    </button>
                  ))}
              </div>
            </section>
            <section className="dp120-rail-card" style={{ ['--r' as string]: '#42d8ff' }}>
              <span className="eyebrow">ЭДВАЙЗЕР / КУРАТОР</span>
              <h3>Сопровождение</h3>
              <p>Организационные вопросы, сроки, группа, практика и общий учебный маршрут.</p>
              <div className="dp120-rail-actions">
                <button
                  type="button"
                  className="dp120-mini"
                  onClick={() => onOpenModal('advisor_contact')}
                >
                  {st('Связь с эдвайзером')}
                </button>
                <button
                  type="button"
                  className="dp120-mini"
                  onClick={() => onOpenModal('meetings')}
                >
                  Встреча
                </button>
              </div>
            </section>
            <section className="dp120-rail-card" style={{ ['--r' as string]: '#f1b93f' }}>
              <span className="eyebrow">ТЕМА</span>
              <h3>{topicTitle}</h3>
              <p>
                {supervisor
                  ? `Руководитель: ${supervisor}`
                  : 'Научный руководитель ещё не заполнен.'}
              </p>
              <div className="dp120-rail-actions">
                <button
                  type="button"
                  className="dp120-mini gold"
                  onClick={() => setActiveView('topic')}
                >
                  Открыть тему
                </button>
              </div>
            </section>
            <section className="dp120-rail-card" style={{ ['--r' as string]: '#9b7cff' }}>
              <span className="eyebrow">ПРОГРЕСС</span>
              <h3>{readiness}% цифрового досье</h3>
              <div className="dp120-progress" style={{ ['--p' as string]: `${readiness}%` }}>
                <i />
              </div>
              <p style={{ marginTop: 7 }}>
                Данные распределены по логическим этапам, а сводная аналитика рассчитывается
                автоматически.
              </p>
            </section>
            <section className="dp120-rail-card" style={{ ['--r' as string]: '#7fd0ff' }}>
              <span className="eyebrow">ОБЩИЕ СЕРВИСЫ</span>
              <h3>Академия</h3>
              <p>Те же файлы, сообщения, календарь и профиль, без второй копии.</p>
              <div className="dp120-next">
                {services.map((item) => (
                  <button
                    key={item.label}
                    type="button"
                    onClick={() => {
                      if (item.view) {
                        setActiveView(item.view);
                        return;
                      }
                      if (item.modal) {
                        onOpenModal(item.modal);
                        return;
                      }
                      if (item.href) onOpenService?.(item.href);
                    }}
                  >
                    <b>·</b>
                    <span>{item.label}</span>
                    <em>→</em>
                  </button>
                ))}
              </div>
            </section>
            <section className="dp120-rail-card" style={{ ['--r' as string]: '#ff7968' }}>
              <span className="eyebrow">ЕСТЬ ПРОБЛЕМА?</span>
              <h3>Нужна помощь</h3>
              <p>Задержка публикации, документа, практики, стажировки или выхода на защиту.</p>
              <div className="dp120-rail-actions">
                <button
                  type="button"
                  className="dp120-mini danger"
                  onClick={() => onOpenModal('problem_report')}
                >
                  Сообщить о проблеме
                </button>
              </div>
            </section>
          </aside>
        </div>
      </div>
    </div>
  );
};
